<template>
  <div
    class="
      tw-w-full
      tw-flex
      tw-items-start
      tw-justify-between
      tw-gap-4
      tw-px-6
      tw-py-3
      tw-bg-sf-neutral-subtle-2
      tw-rounded-b-lg
    "
  >
    <div>
      <FooterLabel for="adjustment-category">{{
        I18n.t('adjustment.modal.adjustment_type')
      }}</FooterLabel>
      <SfMultiselect
        v-model="category"
        name="adjustment-category"
        mode="single"
        :can-clear="false"
        :clear-on-select="false"
        :close-on-select="true"
        :disabled="isDisabled"
        :hide-selected="true"
        :options="categoryOptions"
        :searchable="false"
        variant="sf-input"
        data-testid="adjustment-type"
      />
    </div>
    <div class="tw-min-w-[300px]">
      <FooterLabel for="adjustment-tags">{{
        I18n.t('adjustment.modal.tags')
      }}</FooterLabel>
      <SfMultiselect
        v-model="selectedTags"
        name="adjustment-tags"
        mode="tags"
        :disabled="isDisabled"
        :options="tagOptions"
        placeholder="add a tag"
        variant="sf-input"
        data-testid="adjustment-tags"
      />
    </div>
    <DocumentUpload :disabled="isDisabled" />
    <div class="tw-flex tw-items-center tw-h-full tw-gap-4">
      <SfButtonIcon
        v-if="deleteUrl"
        :disabled="isLoading"
        icon="trash"
        data-testid="delete-adjustment-button"
        @click="handleDelete"
      />
      <SfButton
        v-if="mode === 'new'"
        variant="default"
        :disabled="isDisabled"
        @click="handleSubmitAndNew"
      >
        {{ I18n.t('adjustment.modal.add_new') }}</SfButton
      >
      <SfButton variant="success" :disabled="isDisabled" @click="handleSubmit">
        {{
          mode === 'new'
            ? I18n.t('adjustment.modal.add')
            : I18n.t('adjustment.modal.update')
        }}
      </SfButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  AdjustmentCategory,
  AdjustmentExistingDocument,
  AdjustmentLocalDocument,
  Tag,
} from '../types'
import { SfConfirm } from '@app/types'
import { isExistingAttachment, isErrorWithBody } from '../predicates'

import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import DocumentUpload from './adjustments-modal-document-upload.vue'
import FooterLabel from './adjustments-modal-footer-label.vue'
import { SfLog } from '@app/adapters/logger'
import {
  persistTag,
  removeTag,
  deleteAdjustment,
  saveAdjustment,
} from '@app/application/adjustments/api'
import SfButton from '@app/shared/vue/components/sf-button/sf-button.vue'
import SfButtonIcon from '@app/shared/vue/components/sf-button/sf-button-icon.vue'
import SfMultiselect from '@app/shared/vue/components/sf-multiselect.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { setSafeJSONValue } from '@app/utils/local-storage'

import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'

interface Props {
  adjustmentId: string | null
  allTags: Tag[]
  categories: AdjustmentCategory[]
  deleteUrl: string
  ledgerId: number
  mode: 'new' | 'edit'
  permanentEntryId: number | null
  purpose: string | null
  tagsAddUrl?: string
  tagsDeleteUrl?: string
  reconciliationTextId: number | null
  referer?: string
  saveUrl: string
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  tagsAddUrl: '',
  tagsDeleteUrl: '',
  referer: '',
  title: '',
})

const I18n = useI18n()

const modalStore = useAdjustmentsModalStore()

const {
  accountEntryId: storedAccountEntryId,
  attachmentObjects,
  attachmentOrder,
  category,
  deletedAttachments,
  hasErrors,
  isLoading,
  parsedValuesbyAdjustmentId,
  isLocked,
  previousTags,
  selectedTags,
  serverErrors,
  subAdjustmentOrder,
  subAdjustments: existingSubAdjustments,
  submitTimestamp,
} = storeToRefs(modalStore)

const isDisabled = computed(() => isLoading.value || isLocked.value)

function handleSubmit() {
  save(props.mode)
}

function handleSubmitAndNew() {
  save('add+new')
}

function handleDelete() {
  window.modalCitizen.closeModalPortal()

  const sfConfirm = window.sfConfirm as SfConfirm<() => void>

  sfConfirm({
    question: window.I18n.t(
      'js.global_confirmation_modal.combined.warning_html',
      { name: props.title }
    ),
    cancelLabel: window.I18n.t('js.global_confirmation_modal.default.cancel'),
    confirmLabel: window.I18n.t(
      'js.global_confirmation_modal.combined.remove_button'
    ),
    onCancel: () => {
      window.modalCitizen.openModalPortal('adjustments-modal', {
        adjustmentId: props.adjustmentId || '',
        mode: 'edit',
      })
    },
    onConfirm: () => {
      deleteAdjustment(props.deleteUrl, props.referer)
    },
  })
}

async function save(mode: 'new' | 'edit' | 'add+new') {
  submitTimestamp.value = Date.now()

  if (hasErrors.value) {
    return
  }

  if (mode === 'add+new') {
    setSafeJSONValue('adjustments-modal', {
      accountEntryId: storedAccountEntryId.value,
      adjustmentId: props.adjustmentId,
      ledgerId: props.ledgerId,
      mode: 'new',
    })
  }

  const { new: documentsToAdd, existing: documentsToKeep } =
    attachmentOrder.value.reduce(
      (acc, docId) => {
        const doc = attachmentObjects.value[docId]
        if (isExistingAttachment(doc)) {
          acc.existing.push(doc)
        } else {
          acc.new.push(doc)
        }

        return acc
      },
      {
        new: [] as AdjustmentLocalDocument[],
        existing: [] as AdjustmentExistingDocument[],
      }
    )

  try {
    isLoading.value = true

    const result = await Promise.all([
      saveAdjustment(props.saveUrl, mode, {
        accountEntryId: storedAccountEntryId.value,
        category: category.value,
        documentsToAdd,
        documentsToDelete: deletedAttachments.value,
        documentsToKeep,
        ledgerId: props.ledgerId,
        permanentEntryId: props.permanentEntryId || undefined,
        previousTags: previousTags.value,
        purpose: props.purpose || undefined,
        reconciliationTextId: props.reconciliationTextId || undefined,
        referer: props.referer,
        subAdjustments: subAdjustmentOrder.value.slice(0, -1).map((id) => ({
          ...existingSubAdjustments.value[id],
          valueCredit:
            parsedValuesbyAdjustmentId.value[id].valueCredit.toString(),
          valueDebit:
            parsedValuesbyAdjustmentId.value[id].valueDebit.toString(),
        })),
        tags: selectedTags.value,
      }),
      ...addTags(),
      ...deleteTags(),
    ])
    if (result?.[0]?.redirectUrl) {
      window.modalCitizen.closeModalPortal()
      window.Turbolinks.visit(result[0].redirectUrl)
    }
  } catch (e) {
    if (isErrorWithBody(e)) {
      serverErrors.value = [...e.body.errors]
    } else {
      SfLog.info('Adjustments::Error saving', {
        error: e,
        extra: {
          firmId: window.Silverfin.currentFirmId,
          companyId: window.Silverfin.currentCompanyId,
          ledgerId: props.ledgerId,
          error: e,
        },
      })
    }
  } finally {
    isLoading.value = false
  }
}

function addTags(): Promise<void | Response>[] {
  if (!props.tagsAddUrl) {
    return [Promise.resolve()]
  }

  const tagsToAdd = selectedTags.value.filter(
    (tag) => !previousTags.value.some((previousTag) => previousTag.id === tag)
  )

  return tagsToAdd.map((tag) => persistTag(props.tagsAddUrl, tag))
}

function deleteTags(): Promise<void | null>[] {
  if (!props.tagsDeleteUrl) {
    return [Promise.resolve()]
  }

  const tagsToDelete = previousTags.value.filter(
    (tag) => !selectedTags.value.includes(tag.id)
  )

  return tagsToDelete.map((tag) => removeTag(props.tagsDeleteUrl, tag.id))
}

const categoryOptions = computed(() => {
  return props.categories.map((category) => {
    return {
      label: category[0],
      value: category[1],
    }
  })
})

const tagOptions = computed(() =>
  props.allTags.map((tag) => {
    return {
      id: tag.id,
      label: tag.name,
      value: tag.id,
    }
  })
)
</script>
